/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PHASE_2_SCHEDULER_UI_ROUTES
 * Date:         2025-01-04
 * Modified By:  Cursor
 *
 * Description:
 * Registered admin routes for scheduler backup and job management pages in client/src/App.tsx.
 *
 * File Size Check:
 * - Current: 405 lines
 * - Adding: ~10 lines
 * - Projected: 415 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/features/admin/scheduler/AdminBackupPage.tsx
 * - client/src/features/admin/scheduler/AdminScheduledJobsPage.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ALLBROADCASTS_SIDEBAR_FILTER
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Change route from /broadcasts to /allbroadcasts.
 *
 * File Size Check:
 * - Current: 384 lines
 * - Adding: ~15 lines
 * - Projected: 399 lines
 * - Decision: Proceed (page component, higher limits apply)
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/BroadcastsListPage.tsx
 * - client/src/features/broadcast/pages/broadcast-details/BroadcastDetailsPage.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DETAILS_ROUTING
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Added route for broadcast details page at /broadcasts/details/:id
 * to enable navigation from dashboard broadcast table.
 *
 * File Size Check:
 * - Current: 354 lines
 * - Adding: ~15 lines
 * - Projected: 369 lines
 * - Decision: Proceed (page component, higher limits apply)
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/features/broadcast/pages/broadcast-details/BroadcastDetailsPage.tsx
 * - client/src/features/dashboard/DashboardBroadcastTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_ADMIN_LOGS_ROUTING
 * Date:         2025-12-14
 * Modified By:  Cursor
 *
 * Description:
 * Fix admin routing so /admin/logs shows System Logs page (multi-service log viewer)
 * instead of Logins/Sessions page. Ensure route order is correct for proper matching.
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/pages/admin/LogsPage.tsx
 * - client/src/pages/admin/LoginsPage.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUTH_ROUTES_SIGNIN_SIGNUP
 * Date:         2025-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Align auth routing so /, /signin, and /signup behave consistently and can be deep-linked.
 * Redirect unauthenticated root to /signin and prevent logged-in users from seeing auth screens.
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/pages/auth/AuthPage.tsx
 * - client/src/components/AuthModal.tsx
 * - client/src/components/auth/SignupForm.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUTH_ROUTES_V1
 * Date:         2025-12-06
 * Modified By:  Cursor (assisted by ChatGPT)
 *
 * Description:
 * Add direct /signin and /signup routes and new AuthPage wrapper, while
 * preserving existing AuthModal behavior for non-auth pages.
 *
 * Related Screens / Modules:
 * - Client → src/App.tsx
 * - Client → src/pages/auth/AuthPage.tsx
 * - Client → src/components/AuthModal.tsx (light refactor only if needed)
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ITEM_DETAIL_ROUTE
 * Date:         2025-01-12
 * Modified By:  Cursor
 *
 * Description:
 * Updated route to use new ItemDetailPage component with three-section layout
 * (Item Information, Company Information, Recent Broadcasts).
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/pages/ItemDetailPage/ItemDetailPage.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toaster configuration for unified toast styling across all notifications.
 * All toast calls now use centralized notify helper from utils/notifications.ts.
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/utils/notifications.ts
 */

import React, { useEffect, useState, useCallback } from "react";
// Global styles now loaded via `styles.scss` in `index.tsx`
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "sonner";
// [2026-02-11] cookie-auth Phase 3: Use checkAuthStatus (cookie-based)
import { checkAuthStatus } from "./utils/authUtils";
// [2026-07-06] GUEST_ACCESS_PHASE1: tell the axios interceptor not to redirect guests
import { setGuestMode } from "./utils/axiosInterceptor";
import Home from "./components/Home";
// import Profile from "./components/Profile";
import PageNotFound from "./components/PageNotFound";
import Search from "./components/search/Search";
// [2025-12-30] Search refactor: Updated import path
import { InventoryPage } from "./features/inventory/inventoryindex";
// [2025-01-12] Item detail: Import new ItemDetailPage component
import ItemDetailPage from "./pages/ItemDetailPage/ItemDetailPage";
// [2025-12-13] Profile Page: Import ProfilePage component
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import BroadcastCreatePage from "./features/broadcast/BroadcastCreatePage";
import BroadcastMinePage from "./features/broadcast/pages/broadcast-mine/BroadcastMinePage"; // [Phase 7]
import BroadcastsListPage from "./features/broadcast/pages/broadcasts-list/BroadcastsListPage"; // [2025-12-31] Phase 2
// [2026-07-13] help-link: route-aware floating help button
import RouteHelpButton from "./components/ui/RouteHelpButton";
import Help from "./components/Help";
import Setup, { SetupProvider } from "./components/Setup";
import ReviewCompanyForm from "./components/setup/ReviewCompanyForm";
import ReviewUserForm from "./components/setup/review-user/ReviewUserForm";
import AppShell from "./components/layout/AppShell";
import AuthModal from "./components/AuthModal";
import AdminLayout from "./features/admin/AdminLayout";
import RequireAdmin from "./features/admin/RequireAdmin";
// [2026-06-23] impersonation: persistent "viewing as user" banner
import ImpersonationBanner from "./features/admin/ImpersonationBanner";
import AdminDashboard from "./pages/admin/AdminDashboard";
import LogsPage from "./pages/admin/LogsPage";
import UsersPage from "./pages/admin/UsersPage";
import AdminInventoryPage from "./features/inventory/AdminInventoryPage";
// ExchangeRatesPage moved under scheduler; old wrapper removed from imports
import LoginsPage from "./pages/admin/LoginsPage";
import Dashboard from "./pages/Dashboard";
import NotificationsPage from "./pages/NotificationsPage";
import MyAlertProfilesPage from "./features/setup/alerts/MyAlertProfilesPage";
import SmartFiltersPage from "./features/setup/alerts/SmartFiltersPage";
import SmartFiltersEditPage from "./features/setup/alerts/SmartFiltersEditPage";
import ResetPassword from "./pages/auth/ResetPassword";
import ForgotPassword from "./pages/auth/ForgotPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";
// [Auth routes / import AuthPage / 2025-12-06]
import AuthPage from "./pages/auth/AuthPage";
import MailRequestsPage from "./pages/admin/MailRequestsPage";
// Admin activity page removed on 2026-01-05
// [2025-12-20] Tracing: Import MonitoringPage
import { MonitoringPage } from "./pages/admin/MonitoringPage";
// [2025-12-20] Phase 3: Import OverviewPage
// OverviewPage removed on 2026-01-05 — import deleted
// [2025-12-29] Broadcast details page
import BroadcastDetailsPage from "./features/broadcast/pages/broadcast-details/BroadcastDetailsPage";
import BroadcastItemDetailPage from "./features/broadcast/pages/broadcast-item-detail/BroadcastItemDetailPage";
// [2025-01-03] Admin UI: Import scheduler admin pages
import AdminBackupPage from "./features/admin/scheduler/AdminBackupPage";
import AdminScheduledJobsPage from "./features/admin/scheduler/AdminScheduledJobsPage";
import BackupFilesPage from "./features/admin/scheduler/BackupFilesPage";
// [2026-01-05] Scheduler routes: Import exchange rates and planned jobs pages
import AdminExchangeRatesPage from "./features/admin/scheduler/AdminExchangeRatesPage";
import AdminPlannedJobsPage from "./features/admin/scheduler/AdminPlannedJobsPage";
// [2026-01-XX] Database stats: Import Database Statistics page
import DatabaseStatisticsPage from "./pages/admin/DatabaseStatisticsPage";
// [2026-07-06] GUEST_ACCESS BR-16: admin Visitors (guest activity) page
import GuestVisitorsPage from "./pages/admin/GuestVisitorsPage";
// [2026-01-28] Phase 6: Import broadcast response pages
import BroadcastResponse from "./pages/Broadcast/BroadcastResponse";
import BroadcastDeleted from "./pages/Broadcast/BroadcastDeleted";
// [2026-02-08] BR-16: Import inventory confirm result page
import InventoryConfirmResult from "./pages/InventoryConfirmResult";
// [2026-06-23] BR-16: Branded confirm landing page (email button targets this, not the API)
import InventoryConfirm from "./pages/InventoryConfirm";
// [2026-02-08] Export polling: Context provider for background export
import { ExportProvider } from "./contexts/ExportContext";
// [2026-03-09] GDPR: Cookie consent banner + analytics loader
import CookieConsentBanner from "./components/CookieConsentBanner/CookieConsentBanner";
import { getCookieConsent, setCookieConsent } from "./utils/cookieConsent";
import { loadAnalytics } from "./utils/analyticsLoader";
import { getCookieConsentFromServer } from "./services/usersService";
// [2026-07-05] GUEST_ACCESS_PHASE1: guest-mode context + landing attribution capture
import { GuestModeContext } from "./contexts/GuestModeContext";
import { captureAttribution } from "./utils/attribution";

const SubscriptionSettingsPage = React.lazy(() => import("./pages/admin/SubscriptionSettingsPage"));
// [2026-03-07] system-params: Lazy-load System Params page
const SystemParamsPage = React.lazy(() => import("./pages/admin/SystemParamsPage"));
// [2026-06-26] blocklist: Lazy-load Blocklist page
const BlocklistPage = React.lazy(() => import("./pages/admin/BlocklistPage"));

// [2026-07-06] GUEST_ACCESS_PHASE1: a guest hitting a member-only route (Inventory,
// Broadcasts, Companies, item detail…) gets a clear "sign in to access" message with
// the sidebar intact — not a silent bounce. (@victor copy)
function GuestGatePrompt() {
  const navigate = useNavigate();
  return (
    <div className="text-center py-5 px-3">
      <div className="display-4 mb-2">🔒</div>
      <h2 className="u-H2-24 mb-2">Sign in to access this</h2>
      <p className="u-P2-16 text-color-grey-1 mb-4">
        Only signed-in members can access this. Sign in or create a free account to continue.
      </p>
      {/* [2026-07-12] guest-gate: Align buttons to standard outlined pill style (btn-figma-1),
          matching the Add Item / Upload File toolbar buttons — was btn-black + Bootstrap btn-secondary */}
      <div className="d-flex gap-2 justify-content-center">
        <button type="button" className="btn-figma-1" onClick={() => navigate("/signup")}>
          Create free account
        </button>
        <button type="button" className="btn-figma-1" onClick={() => navigate("/signin")}>
          Sign in
        </button>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [dashboardKey, setDashboardKey] = useState(0);
  // [2026-06-23] impersonation: when an admin is "viewing as" a user, hold their display name
  const [impersonatingName, setImpersonatingName] = useState<string | null>(null);

  // [2026-02-11] cookie-auth Phase 3: Check auth status via API (cookie-based)
  const verifyAuth = useCallback(async () => {
    setIsAuthChecking(true);
    const user = await checkAuthStatus();
    const loggedIn = !!user;
    setIsLoggedIn(loggedIn);
    // [2026-07-06] GUEST_ACCESS_PHASE1: guests must never be hard-redirected to /signin on a 401
    setGuestMode(!loggedIn);
    // [2026-06-23] impersonation: surface "viewing as user" state for the banner
    setImpersonatingName(
      user?.isImpersonating
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "user"
        : null,
    );
    setIsAuthChecking(false);

    // [2026-03-09] GDPR: On login, restore cookie consent from server if not in localStorage
    if (loggedIn && !getCookieConsent()) {
      try {
        const { data } = await getCookieConsentFromServer();
        if (data.cookieConsent?.value) {
          setCookieConsent(data.cookieConsent.value as "accepted" | "declined");
          if (data.cookieConsent.value === "accepted") {
            loadAnalytics();
          }
        }
      } catch {
        // Non-critical — banner will show if no local consent
      }
    }

    return loggedIn;
  }, []);

  // [2026-07-05] GUEST_ACCESS_PHASE1: capture campaign attribution on first landing (BR-19)
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      await verifyAuth();

      // Close the auth modal on the dedicated auth pages
      const isAuthPage =
        location.pathname === "/reset-password" ||
        location.pathname === "/forgot" ||
        location.pathname === "/verify-email" ||
        location.pathname === "/signin" ||
        location.pathname === "/signup";
      if (isAuthPage) setIsAuthModalOpen(false);
      // [2026-07-05] GUEST_ACCESS_PHASE1: guests are NO LONGER redirected to /signin —
      // the public AppShell renders for them (BR-01/BR-06). Sign-up is prompted only
      // on gated actions (handled by the guest routes below).
    };
    checkAuth();
  }, [location.pathname, verifyAuth]);

  // Check for showLogin query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("showLogin") === "true") {
      setIsAuthModalOpen(true);
      // Clean up the URL by removing the query parameter
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location]);

  const handleAuthSuccess = () => {
    setIsLoggedIn(true);
    setIsAuthModalOpen(false);
    // Increment dashboard key to force remount and refresh data
    setDashboardKey((prev) => prev + 1);
  };

  const handleModalClose = () => {
    setIsAuthModalOpen(false);
  };

  return (
    <>
      {/* [2026-06-23] impersonation: persistent read-only banner while viewing as a user */}
      {isLoggedIn && impersonatingName && (
        <ImpersonationBanner userName={impersonatingName} />
      )}
      <Routes>
        {/* Auth routes - outside AppShell for public access */}
        {/* [2025-01-27] auth-routes: prevent logged-in users from viewing signin/signup pages */}
        <Route
          path="/signin"
          element={isLoggedIn ? <Navigate to="/" replace /> : <AuthPage initialTab="signin" />}
        />
        <Route
          path="/signup"
          element={isLoggedIn ? <Navigate to="/" replace /> : <AuthPage initialTab="signup" />}
        />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* [2026-02-08] BR-16: Inventory confirm result page (public, accessed from email) */}
        <Route path="/inventory/confirm-result" element={<InventoryConfirmResult />} />
        {/* [2026-06-23] BR-16: Branded confirm landing page (public, email button target) */}
        <Route path="/inventory/confirm" element={<InventoryConfirm />} />

        {/* All routes wrapped in AppShell (including admin) */}
        <Route
          path="/*"
          element={
            isAuthChecking ? (
              // [2026-02-11] cookie-auth Phase 3: Show loading while checking auth
              <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : isLoggedIn ? (
              <GuestModeContext.Provider value={false}>
              <AppShell>
                {/* [2026-07-13] help-link: route-aware floating "?" on Inventory/Broadcasts */}
                <RouteHelpButton />
                <Routes>
                  <Route path="/" element={<Dashboard key={dashboardKey} />} />
                  <Route path="/home" element={<Home isLoggedIn={isLoggedIn} />} />
                  <Route path="/search" element={<Search />} />
                  {/* [2025-01-12] Item detail: Use new ItemDetailPage with three-section layout */}
                  <Route path="/inventory/item/:id" element={<ItemDetailPage />} />
                  {/* [2025-12-13] Profile Page: Add profile page route */}
                  <Route path="/profile/:userId" element={<ProfilePage />} />
                  <Route path="/inventory" element={<InventoryPage />} />
                  {/* [2026-01-01] Route changed from /broadcasts to /allbroadcasts */}
                  <Route path="/allbroadcasts" element={<BroadcastsListPage />} />
                  {/* [Phase 7] Broadcast sub-routes: /new landing, /mine-all list, /edit form */}
                  <Route path="/broadcast/new" element={<BroadcastCreatePage />} />
                  <Route path="/broadcast/mine-all" element={<BroadcastMinePage />} />
                  <Route path="/broadcast/edit" element={<BroadcastCreatePage />} />
                  <Route path="/broadcast" element={<Navigate to="/broadcast/new" replace />} />
                  {/* [2026-01-01] Route changed to /allbroadcasts/details/:id */}
                  <Route path="/allbroadcasts/details/:id" element={<BroadcastDetailsPage />} />
                  <Route
                    path="/allbroadcasts/item/:broadcastId/:itemNo"
                    element={<BroadcastItemDetailPage />}
                  />
                  {/* [2026-01-28] Phase 6: Broadcast response routes (BR-13, BR-19) */}
                  <Route path="/broadcast/:id/respond" element={<BroadcastResponse />} />
                  <Route path="/broadcast/:id/deleted" element={<BroadcastDeleted />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/setup" element={<Setup />} />
                  <Route
                    path="/setup/reviewcompany"
                    element={
                      <SetupProvider>
                        <ReviewCompanyForm />
                      </SetupProvider>
                    }
                  />
                  <Route
                    path="/setup/reviewuser"
                    element={
                      <SetupProvider>
                        <ReviewUserForm />
                      </SetupProvider>
                    }
                  />
                  <Route path="/alerts" element={<MyAlertProfilesPage />} />
                  <Route path="/alerts/smart/new" element={<SmartFiltersPage />} />
                  <Route path="/alerts/smart/edit/:id" element={<SmartFiltersEditPage />} />

                  {/* Admin Routes - Now inside AppShell */}
                  <Route
                    path="/admin/*"
                    element={
                      <RequireAdmin>
                        <AdminLayout />
                      </RequireAdmin>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    {/* [2025-12-20] Phase 3: Add overview route */}
                    {/* Overview route removed on 2026-01-05 */}
                    <Route path="users" element={<UsersPage />} />
                    <Route path="inventory" element={<AdminInventoryPage />} />
                    {/* [2026-01-XX] Database stats: Add database statistics route */}
                    <Route path="database-statistics" element={<DatabaseStatisticsPage />} />
                    {/* [2026-07-06] GUEST_ACCESS BR-16: admin Visitors (guest activity) */}
                    <Route path="guests" element={<GuestVisitorsPage />} />
                    {/* Old route /admin/exchange-rates removed - replaced by scheduler route */}
                    {/* Admin activity route removed on 2026-01-05 */}
                    {/* [2025-12-14] Admin routing: System Logs route - must come before nested routes and logins route for proper matching */}
                    <Route path="logs" element={<LogsPage />} />
                    {/* [2025-12-14] Admin routing: Nested route for mail logs - must come after parent logs route */}
                    <Route path="logs/mail" element={<MailRequestsPage />} />
                    {/* [2025-12-14] Admin routing: Logins/Sessions route - separate from System Logs, must come after logs routes */}
                    <Route path="logins" element={<LoginsPage />} />
                    {/* [2025-12-20] Tracing: Add monitoring route */}
                    <Route path="monitoring" element={<MonitoringPage />} />
                    {/* [2025-01-03] Admin UI: Scheduler routes */}
                    <Route path="scheduler/backup" element={<AdminBackupPage />} />
                    <Route path="scheduler/backup-files" element={<BackupFilesPage />} />
                    {/* [2026-01-05] Scheduler routes: Add exchange rates and planned jobs routes */}
                    <Route path="scheduler/exchange-rates" element={<AdminExchangeRatesPage />} />
                    <Route path="scheduler/planned-jobs" element={<AdminPlannedJobsPage />} />
                    <Route path="scheduler/jobs" element={<AdminScheduledJobsPage />} />
                    {/* [2025-12-20] Phase 3: Add analytics route */}
                    <Route 
                      path="analytics" 
                      element={
                        <div className="container mt-4">
                          <h1>Analytics</h1>
                          <p className="text-muted">Analytics dashboard coming soon...</p>
                        </div>
                      } 
                    />
                    <Route
                      path="subscriptions"
                      element={
                        <React.Suspense fallback={null}>
                          <SubscriptionSettingsPage />
                        </React.Suspense>
                      }
                    />
                    {/* [2026-03-07] system-params: Add System Params route */}
                    <Route
                      path="system-params"
                      element={
                        <React.Suspense fallback={null}>
                          <SystemParamsPage />
                        </React.Suspense>
                      }
                    />
                    {/* [2026-06-26] blocklist: Add Blocklist route */}
                    <Route
                      path="blocklist"
                      element={
                        <React.Suspense fallback={null}>
                          <BlocklistPage />
                        </React.Suspense>
                      }
                    />
                  </Route>

                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </AppShell>
              </GuestModeContext.Provider>
            ) : (
              // [2026-07-05] GUEST_ACCESS_PHASE1: public landing for guests (BR-01/BR-06) —
              // no forced /signin redirect. Dashboard + Search are public; other routes prompt sign-up.
              <GuestModeContext.Provider value={true}>
                <AppShell>
                  <Routes>
                    <Route path="/" element={<Dashboard key={dashboardKey} />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="*" element={<GuestGatePrompt />} />
                  </Routes>
                </AppShell>
              </GuestModeContext.Provider>
            )
          }
        />
      </Routes>

      {/* Auth Modal - automatically opens when not logged in */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleModalClose}
        initialTab="signin"
        onAuthSuccess={handleAuthSuccess}
      />

      {/* [2026-03-09] GDPR: Cookie consent banner */}
      <CookieConsentBanner isLoggedIn={isLoggedIn} />

      {/* Global toast container - unified styling for all notifications */}
      {/* [2025-01-XX] ui-standardization: Unified toast styling applied globally */}
      <Toaster
        position="top-center"
        duration={6000}
        toastOptions={{
          className: "toast-default u-P3-14",
          // Global toast styling - consistent across all notification types
          style: {
            maxWidth: "70vw", // allow toast to span more of the screen width
            width: "fit-content", // grow automatically based on text
            whiteSpace: "pre-wrap", // allow multi-line text
            padding: "12px 16px",
            borderRadius: "8px", // consistent border radius
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ExportProvider>
        <Router>
          <AppContent />
        </Router>
      </ExportProvider>
    </div>
  );
}

export default App;
